import { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import axiosDotnet from '../services/axiosDotnet';  
import useAuth from '../Hooks/auth/useAuth';
import { forgeAssistant } from '../services/groqService';
import { showSuccess, showWarning } from '../services/reactToastify';

export default function CreateProject() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const cwId = useParams().wId;
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentWorkSpace, setCurrentWorkSpace] = useState();

  useEffect(() => {
    const fetchCWorkspace = async () => {
      setLoading(true);
      try {
        const response = await axiosDotnet.get('/api/workspaces');
        setCurrentWorkSpace(response?.data?.filter(w => w?.id == cwId)[0])

      } catch (err) {
        console.error(err?.response);
        setError(err?.response?.data?.message || 'Failed to fetch workspaces');
      } finally {
        setLoading(false);
      }
    };

    if (auth?.foundUser?.id) {
      fetchCWorkspace();
    }

  }, [auth?.foundUser?.id])

  // console.log(currentWorkSpace)

  // ================= AI Integrate ===============>

  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');

  useEffect(() => {

    const projectPromptTemplate = `
You are an expert Project Manager and Technical Architect.

**Workspace Name:** ${currentWorkSpace?.name}
**Workspace Description:** ${currentWorkSpace?.description}

Task:
Create **ONE unique, specific, and actionable project** that lives **inside** this workspace.

Important Rules:
- Do NOT repeat or copy ideas from previous responses.
- The project must be **related** to the workspace but **NOT a copy** of it.
- Do NOT use words like "Platform", "Hub", "System", or "Community" in the title if they are already in the workspace name.
- Make the project title and description **more specific and focused**.
- Be creative and specific.
- The project should be different every time this prompt is called.
- Think of a **concrete initiative** or deliverable within this workspace.

Respond **only** with valid JSON in this exact format. No extra text, no explanations, no markdown.

{
  "Title": "Unique and specific project title",
  "Description": "Detailed, focused project description (2-3 sentences)",
  "Tools": ["Tool1", "Tool2", "Tool3"],
  "Technologies": ["Tech1", "Tech2", "Tech3"],
  "Requirements": {
    "Hardware": "Brief hardware requirement",
    "Software": "Brief software requirement",
    "Networking": "Brief networking requirement"
  }
}
`;

    const randomSeed = Math.random().toString(36).substring(2, 10);

    const finalPrompt = projectPromptTemplate + `\n\nGenerate a completely new idea. Random seed: ${randomSeed}`

    setPrompt(finalPrompt);

  }, [currentWorkSpace])
  // console.log(prompt)
  const handleAsk = async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    try {
      const result = await forgeAssistant(prompt);

      console.log("Raw response from Groq:", result);
      let parsedData;

      if (typeof result === 'object' && result !== null) {
        parsedData = result;
      }
      else if (typeof result === 'string') {
        let cleanResult = result
          .replace(/```json\n?/g, '')
          .replace(/```\n?/g, '')
          .trim();

        parsedData = JSON.parse(cleanResult);
      }

      setResponse(parsedData);
      // console.log("Successfully parsed JSON:", parsedData);
      setProjectName(parsedData?.Title)
      setDescription(parsedData?.Description)
    } catch (err) {
      setResponse("Sorry, AI is currently unavailable.");
    } finally {
      setLoading(false);
    }
  }


  // ================= AI Integrate ===============>

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!projectName.trim()) {
      setError('Project name is required');
      showWarning('Project name is required')
      return;
    }

    if (!auth?.foundUser?.id) {
      setError('User not authenticated. Please login again.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // console.log(projectName, description)

      const response = await axiosDotnet.post('/api/projects', {
        UserId: auth?.foundUser?.id,
        WorkspaceId: cwId,
        Title: projectName.trim(),
        Description: description.trim() || null,
      });

      // console.log('Project created:', response?.data);

      showSuccess('Project Created Successfully!');
      navigate(`/workspace/${cwId}`);
    }
    catch (err) {
      console.error(err?.response);
      setError(
        err?.response?.data?.message ||
        err?.response?.data ||
        'Failed to create project. Please try again.'
      );
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Link to={`/workspace/${cwId}`}>
          <button className="mt-5 ml-5 cursor-pointer px-8 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-2xl font-xxl">
            ←
          </button>
        </Link>
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6 py-14">
      
      <div className="w-full max-w-md">
        
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold">A</div>
            <span className="text-3xl font-semibold tracking-tight text-white">AetherForge</span>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-10 shadow-2xl">
          <h2 className="text-3xl font-semibold text-center text-white mb-2">
            Create New Project
          </h2>
          <p className="text-zinc-400 text-center mb-8">
            Create project for your team to collaborate on
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-full text-white bg-zinc-950 border border-zinc-700 focus:border-violet-500 rounded-2xl px-5 py-4 outline-none transition-colors"
                placeholder="e.g. Product Team"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={7}
                className="w-full text-white bg-zinc-950 border border-zinc-700 focus:border-violet-500 rounded-2xl px-5 py-4 outline-none transition-colors resize-none"
                placeholder="What will this workspace be used for?"
              />
            </div>

            {/* Error Message */}
            {/* {error && (
              <p className="text-red-500 text-sm text-center bg-red-950/50 border border-red-900 rounded-2xl p-3">
                {error}
              </p>
            )} */}

            <button
              type="submit"
              disabled={loading || !projectName?.trim()}
              className="w-full cursor-pointer bg-violet-600 hover:bg-violet-500 disabled:bg-violet-800 py-4 rounded-2xl font-semibold text-lg transition-all"
            >
              {loading ? 'Creating Project...' : 'Create Project'}

            </button>

          </form>

          <button
            onClick={handleAsk}
            disabled={loading}
            className="w-full mt-5 cursor-pointer bg-violet-600 hover:bg-violet-500 disabled:bg-violet-800 py-4 rounded-2xl font-semibold text-lg transition-all"
          >
            AI Project Suggestions
          </button>

          <p className="text-center text-zinc-400 mt-8">
            Want to join an existing workspace?{' '}
            <Link to='/dashboard'>
              <button
                onClick={() => navigate('/workspaces')}
                className=" cursor-pointer text-violet-400 hover:text-violet-300 font-medium"
              >
                Browse Workspaces
              </button>
            </Link>
          </p>
        </div>
      </div>
    </div>
    </div>
  );
}
